import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * Oficina do Iô: WhatsApp com formulário (snippets/oi-whatsapp-lead.liquid), como o plugin Click to Chat do site
 * WordPress: nome e telefone antes de abrir a conversa, para clique acidental não virar contato.
 *
 * O botão flutuante e todos os links de WhatsApp da loja (`wa.me`, `api.whatsapp.com`) abrem o formulário; o texto
 * do link vira o começo da mensagem. Sem JavaScript, os links abrem o WhatsApp direto.
 *
 * Ao enviar o formulário:
 * - publica `oficina:whatsapp_contact` (sem nome nem telefone), que o pixel personalizado
 *   (`store/pixels/oficina-whatsapp-ga4.js`) envia ao GA4 como `whatsapp_contact`, a conversão importada no Google Ads;
 * - envia o gclid da visita ao webhook (Make) em `value1`, como o plugin; nome e telefone só com a opção ligada;
 * - abre o WhatsApp com a mensagem, a página e a referência do anúncio ("Ref": gclid, ou gbraid/wbraid em cliques
 *   do iPhone), mais nome e telefone. A equipe copia o Ref para a planilha de vendas quando o cliente compra.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - A janela.
 * @property {HTMLFormElement} form - Formulário.
 * @property {HTMLInputElement} name - Campo do nome.
 * @property {HTMLInputElement} phone - Campo do telefone, formatado enquanto se digita: "(31)99162-0987".
 * @property {HTMLElement} nameError - Erro do nome, ligado ao campo por `aria-describedby`.
 * @property {HTMLElement} phoneError - Erro do telefone, ligado ao campo por `aria-describedby`.
 *
 * @extends {DialogComponent}
 */
class OiWhatsappLead extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'name', 'phone', 'nameError', 'phoneError'];

  /** Mensagem e origem do clique que abriu a janela. */
  #pending = { message: '', location: 'flutuante' };

  /** @type {AbortController | undefined} */
  #listeners;

  /** Acompanha o teclado do celular enquanto a janela está aberta. @type {AbortController | undefined} */
  #keyboard;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    // Fase de captura: o formulário entra antes de qualquer outro clique no link.
    document.addEventListener('click', this.#onLinkClick, { capture: true, signal: this.#listeners.signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#unwatchKeyboard, { signal: this.#listeners.signal });
    whenConsentReady(storeAdClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#unwatchKeyboard();
  }

  get #formEnabled() {
    return this.dataset.form !== 'false';
  }

  /** Botão flutuante. */
  openFloat() {
    this.#open(this.dataset.message ?? '', 'flutuante');
  }

  /**
   * Links de WhatsApp em qualquer lugar da página.
   *
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !isWhatsappUrl(link.href)) return;

    const message = new URL(link.href).searchParams.get('text') ?? this.dataset.message ?? '';
    const location = link.dataset.oiWaLocation || 'link';
    if (!this.#formEnabled) {
      this.#track(location);
      return;
    }
    event.preventDefault();
    this.#open(message, location);
  };

  /**
   * @param {string} message - Começo da mensagem.
   * @param {string} location - Origem do clique (flutuante, produto, link).
   */
  #open(message, location) {
    this.#pending = { message, location };
    if (!this.#formEnabled) {
      this.#track(location);
      this.#openWhatsapp(message);
      return;
    }
    this.#setError('name', '');
    this.#setError('phone', '');
    this.showDialog();
    this.#watchKeyboard();
    // No desktop, o foco vai para o nome depois que a janela abre. No celular fica no padrão do `showModal`, para o
    // teclado não cobrir o painel antes de a pessoa ler.
    if (window.matchMedia('(min-width: 750px)').matches) {
      requestAnimationFrame(() => requestAnimationFrame(() => this.refs.name.focus()));
    }
  }

  /**
   * O painel inferior fica `fixed` no viewport de layout, que o teclado do celular cobre sem encolher. Mede a parte
   * coberta pelo `visualViewport` e passa para o CSS: `--oi-wa-keyboard` sobe o painel e `data-keyboard` o compacta.
   */
  #watchKeyboard() {
    const viewport = window.visualViewport;
    if (!viewport) return;
    this.#keyboard?.abort();
    this.#keyboard = new AbortController();
    const { dialog } = this.refs;
    const update = () => {
      const covered = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      // Abaixo disso é a barra do navegador recolhendo, não o teclado.
      const open = covered > 120;
      dialog.style.setProperty('--oi-wa-keyboard', `${open ? Math.round(covered) : 0}px`);
      dialog.toggleAttribute('data-keyboard', open);
    };
    viewport.addEventListener('resize', update, { signal: this.#keyboard.signal });
    viewport.addEventListener('scroll', update, { signal: this.#keyboard.signal });
    update();
  }

  #unwatchKeyboard = () => {
    this.#keyboard?.abort();
    this.#keyboard = undefined;
    this.refs.dialog?.style.removeProperty('--oi-wa-keyboard');
    this.refs.dialog?.removeAttribute('data-keyboard');
  };

  /**
   * Envio do formulário.
   *
   * @param {SubmitEvent} event
   */
  submit(event) {
    event.preventDefault();
    const { name, phone } = this.refs;
    const nameValue = name.value.trim().replace(/\s+/g, ' ');
    const phoneDigits = toPhoneDigits(phone.value);

    // Os dois erros aparecem juntos; o foco vai para o primeiro campo com problema.
    const nameOk = nameValue.length >= 2;
    const phoneOk = phoneDigits.length === 10 || phoneDigits.length === 11;
    this.#setError('name', nameOk ? '' : (this.dataset.textInvalidName ?? ''));
    this.#setError('phone', phoneOk ? '' : (this.dataset.textInvalidPhone ?? ''));
    if (!nameOk || !phoneOk) {
      (nameOk ? phone : name).focus();
      return;
    }

    const { message, location } = this.#pending;
    const ref = marketingAllowed() ? readAdClick().ref : '';
    const lines = [`${this.dataset.labelPage}: ${window.location.origin}${window.location.pathname}`];
    if (ref) lines.push(`${this.dataset.labelRef}: ${ref}`);
    lines.push(`${this.dataset.labelName}: ${nameValue}`, `${this.dataset.labelPhone}: ${formatPhoneDigits(phoneDigits)}`);
    const text = [message.trim(), lines.join('\n')].filter(Boolean).join('\n\n');

    this.#track(location, { name: nameValue, phone: phoneDigits });
    this.#openWhatsapp(text);
    this.refs.form.reset();
    delete phone.dataset.digits;
    this.closeDialog();
  }

  /**
   * Formata o telefone enquanto se digita: "(31)99162-0987" (celular) ou "(11)3456-1234" (fixo). O cursor fica
   * depois do mesmo dígito; apagar um parêntese ou hífen apaga o dígito anterior.
   *
   * @param {InputEvent} event
   */
  formatPhone(event) {
    const input = this.refs.phone;
    const caret = input.selectionStart ?? input.value.length;
    const raw = input.value.replace(/\D/g, '');
    let digits = toPhoneDigits(input.value);
    // Dígitos tirados do começo (55 do DDI, zero de operadora) não contam para a posição do cursor.
    const lead = Math.max(0, raw.indexOf(digits));
    const rawBeforeCaret = input.value.slice(0, caret).replace(/\D/g, '').length;
    let digitsBeforeCaret = Math.min(digits.length, Math.max(0, rawBeforeCaret - lead));

    // Apagar só um parêntese ou hífen não muda os dígitos: apaga o dígito vizinho.
    const previous = input.dataset.digits ?? '';
    if (digits === previous && event.inputType === 'deleteContentBackward' && digitsBeforeCaret > 0) {
      digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
      digitsBeforeCaret -= 1;
    } else if (digits === previous && event.inputType === 'deleteContentForward') {
      digits = digits.slice(0, digitsBeforeCaret) + digits.slice(digitsBeforeCaret + 1);
    }
    input.dataset.digits = digits;
    input.value = formatPhoneDigits(digits);

    let position = 0;
    for (let seen = 0; position < input.value.length && seen < digitsBeforeCaret; position++) {
      if (/\d/.test(input.value.charAt(position))) seen++;
    }
    if (document.activeElement === input) input.setSelectionRange(position, position);
    if (digits.length >= 10) this.#setError('phone', '');
  }

  /**
   * Apaga o erro do nome quando ele passa a valer.
   *
   * @param {InputEvent} event
   */
  clearError(event) {
    if (event.target === this.refs.name && this.refs.name.value.trim().length >= 2) this.#setError('name', '');
  }

  /**
   * @param {'name' | 'phone'} field
   * @param {string} message - Vazio apaga o erro.
   */
  #setError(field, message) {
    const input = field === 'name' ? this.refs.name : this.refs.phone;
    const error = field === 'name' ? this.refs.nameError : this.refs.phoneError;
    error.textContent = message;
    error.hidden = !message;
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
  }

  /**
   * Evento para os pixels e webhook. Nome e telefone nunca vão para o evento.
   *
   * @param {string} location
   * @param {{ name: string, phone: string }} [lead]
   */
  #track(location, lead) {
    const data = {
      button_location: location,
      page_type: this.dataset.pageType ?? '',
      product_title: this.dataset.productTitle ?? '',
    };
    try {
      window.Shopify?.analytics?.publish?.('oficina:whatsapp_contact', data);
    } catch (e) {}

    const webhook = this.dataset.webhook;
    if (!webhook) return;
    const body = new URLSearchParams({ value1: marketingAllowed() ? readAdClick().gclid : '' });
    if (lead && this.dataset.webhookLead === 'true') {
      body.set('nome', lead.name);
      body.set('telefone', lead.phone);
      body.set('pagina', window.location.href);
      body.set('origem', location);
    }
    // Requisição simples (form-urlencoded, no-cors), como o plugin: o webhook não precisa responder com CORS.
    fetch(webhook, { method: 'POST', mode: 'no-cors', keepalive: true, body }).catch(() => {});
  }

  /** @param {string} text */
  #openWhatsapp(text) {
    const url = `https://wa.me/${this.dataset.phone}?text=${encodeURIComponent(text)}`;
    const opened = window.open(url, '_blank');
    if (opened) opened.opener = null;
    else window.location.href = url;
  }
}

/**
 * Só os dígitos do telefone brasileiro, até 11 (DDD + número). Tira o 55 de um número colado com DDI e o zero
 * de operadora ("031…").
 *
 * @param {string} value
 */
function toPhoneDigits(value) {
  let digits = value.replace(/\D/g, '');
  if (digits.length > 11 && digits.startsWith('55')) digits = digits.slice(2);
  digits = digits.replace(/^0+/, '');
  return digits.slice(0, 11);
}

/**
 * "(31)99162-0987" com 11 dígitos; "(11)3456-1234" com 10. Durante a digitação, até 10 dígitos o hífen fica depois
 * do quarto número; o 11º passa para o quinto.
 *
 * @param {string} digits
 */
function formatPhoneDigits(digits) {
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 4) return `(${ddd})${rest}`;
  const split = rest.length === 9 ? 5 : 4;
  return `(${ddd})${rest.slice(0, split)}-${rest.slice(split)}`;
}

/** @param {string} href */
function isWhatsappUrl(href) {
  try {
    const { hostname } = new URL(href);
    return hostname === 'wa.me' || hostname === 'api.whatsapp.com' || hostname === 'web.whatsapp.com';
  } catch (e) {
    return false;
  }
}

const AD_CLICK_KEY = 'oi-gclid';
const AD_CLICK_DAYS = 90;
const AD_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'];

/**
 * Guarda por 90 dias, como o cookie do Google Ads, o identificador do clique no anúncio: `gclid` ou, em cliques
 * do iPhone, `gbraid`/`wbraid`.
 */
function storeAdClick() {
  if (!marketingAllowed()) return;
  const click = adClickFromUrl();
  if (!click.ref) return;
  try {
    localStorage.setItem(AD_CLICK_KEY, JSON.stringify({ ...click, at: Date.now() }));
  } catch (e) {}
}

/** @returns {{ ref: string, gclid: string }} */
function adClickFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const ref = AD_CLICK_PARAMS.map((name) => params.get(name)).find(Boolean) ?? '';
  return { ref, gclid: params.get('gclid') ?? '' };
}

/** Clique de anúncio desta visita (URL) ou o guardado nos últimos 90 dias. */
function readAdClick() {
  const fromUrl = adClickFromUrl();
  if (fromUrl.ref) return fromUrl;
  try {
    const saved = JSON.parse(localStorage.getItem(AD_CLICK_KEY) ?? 'null');
    if (saved && Date.now() - saved.at < AD_CLICK_DAYS * 864e5) {
      // Registro antigo ({ value }) guardava só o gclid.
      const gclid = saved.gclid ?? saved.value ?? '';
      return { ref: saved.ref ?? gclid, gclid };
    }
  } catch (e) {}
  return { ref: '', gclid: '' };
}

/** Consentimento de marketing pela API de privacidade da Shopify; sem a API (sem banner), vale como permitido. */
function marketingAllowed() {
  const privacy = window.Shopify?.customerPrivacy;
  return typeof privacy?.marketingAllowed === 'function' ? privacy.marketingAllowed() : true;
}

/** @param {() => void} callback */
function whenConsentReady(callback) {
  const loadFeatures = window.Shopify?.loadFeatures;
  if (typeof loadFeatures !== 'function') {
    callback();
    return;
  }
  loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => callback());
}

if (!customElements.get('oi-whatsapp-lead')) {
  customElements.define('oi-whatsapp-lead', OiWhatsappLead);
}
